import React from 'react';

import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { Box, Flex } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';

import messages from './messages';

const styles = cssMap({
	// Matches PinnedWorkSection's `emptyState` box exactly, so this shimmers in place without
	// a layout shift once loading resolves — most users won't have pinned anything yet, so
	// the empty-state shape (not the populated list) is the more likely outcome to anticipate.
	container: {
		width: '100%',
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		borderRadius: token('radius.xxlarge'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
	message: {
		flex: '1',
	},
});

/**
 * Loading placeholder for `PinnedWorkSection`, shown while `usePinnedItems`'s initial
 * `listPinsFetch` is in flight — mirrors the empty-state box shape rather than the populated
 * list, since a first-time (no pins yet) result is the more common outcome.
 */
export const PinnedWorkSkeleton = (): React.JSX.Element => {
	const intl = useIntl();

	return (
		<Box
			xcss={styles.container}
			role="group"
			aria-label={intl.formatMessage(messages.pinnedWorkLoadingLabel)}
			testId="rovo-for-you-pinned-work-skeleton"
		>
			<Flex alignItems="center" justifyContent="space-between" gap="space.150">
				<Box xcss={styles.message}>
					<Skeleton width="70%" height={16} borderRadius={token('radius.small')} isShimmering />
				</Box>
				<Skeleton width={80} height={32} borderRadius={token('radius.small')} isShimmering />
			</Flex>
		</Box>
	);
};
